import LazyVideo from "@/components/LazyVideo";
import React, { useState, useEffect, useRef } from 'react';
import { Seo } from '../components/Seo';
import { clinicInfo, providerRef } from '../data/clinicInfo';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { type Product, productRecommendations } from '../data/content';
import { useContent } from '@/context/ContentContext';
import {
  ArrowLeft,
  Leaf,
  ShoppingBag,
  Check,
  ShieldCheck,
  Droplets,
  Sparkles,
  Plus,
  Minus,
  FlaskConical,
  HandHeart,
  Package,
  Droplet,
  ClipboardList,
  MessageCircle,
  Truck,
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import productBanner from '@/assets/product-banner.jpg';

const ParallaxVideoBanner: React.FC<{ src: string }> = ({ src }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], ['-30%', '30%']);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.25, 1.18, 1.25]);

  return (
    <section
      ref={ref}
      className="relative mb-20 md:mb-28 overflow-hidden h-[460px] md:h-[680px] lg:h-[820px]"
      style={{ width: '100vw', marginInline: 'calc(50% - 50vw)' }}
    >
      <motion.video
        src={src}
        autoPlay
        muted
        loop
        playsInline
        style={{ y, scale }}
        className="absolute inset-0 w-full h-[170%] -top-[35%] object-cover will-change-transform"
      />
    </section>
  );
};

const FAQItem: React.FC<{ q: string; a: string; index: number }> = ({ q, a, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-5% 0px' }}
      transition={{ duration: 0.45, delay: index * 0.05 }}
      className="border-b border-[#412018]/15 last:border-b-0"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-6 py-7 md:py-8 text-right group/q"
      >
        <span className="font-serif text-[#412018] text-[2.2rem] md:text-[2.4rem] lg:text-[3rem] leading-[1.15] flex-1 group-hover/q:text-[#67392b] transition-colors">
          {q}
        </span>
        <span className="w-10 h-10 rounded-full border border-[#412018]/25 flex items-center justify-center text-[#67392b] shrink-0 group-hover/q:bg-[#67392b] group-hover/q:text-[#EAE6D7] transition-colors duration-300">
          {open ? <Minus size={16} strokeWidth={1.6} /> : <Plus size={16} strokeWidth={1.6} />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="text-[#412018]/75 font-light text-[16px] md:text-[17px] leading-[1.85] pb-8 max-w-[55ch]">
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const CollapsibleDescription: React.FC<{ heading?: string; paragraphs: string[] }> = ({
  heading,
  paragraphs,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-6">
      {heading && (
        <h3 className="font-serif text-[#412018] text-[1.9rem] md:text-[2.2rem] leading-[1.1] mb-4">
          {heading}
        </h3>
      )}
      <div
        className={`relative space-y-4 text-[16px] md:text-[17px] text-[#412018]/80 font-light leading-[1.85] ${
          open ? '' : 'line-clamp-4'
        }`}
      >
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
      <button
        onClick={() => setOpen((v) => !v)}
        className="mt-4 inline-flex items-center gap-2 text-[#67392b] hover:text-[#412018] transition-colors text-[12px] uppercase tracking-[0.28em] font-bold"
      >
        {open ? <Minus size={14} /> : <Plus size={14} />}
        {open ? 'הסתר' : 'קרא עוד'}
      </button>
    </div>
  );
};

export const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { products: productsData, shopCategories } = useContent();
  const product = productsData.find((p) => p.id === id);
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);
  const [bundleAdded, setBundleAdded] = useState(false);
  const [addedItems, setAddedItems] = useState<{ [key: string]: boolean }>({});
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [productGalleryIdx, setProductGalleryIdx] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const onScroll = () => setShowStickyBar(window.scrollY > window.innerHeight * 0.7);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!product) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#EAE6D7] text-[#412018]">
        <div className="text-center">
          <h1 className="text-4xl font-serif mb-4">המוצר לא נמצא</h1>
          <Link to="/shop" className="text-[#67392b] hover:underline flex items-center gap-2 justify-center">
            <ArrowLeft size={16} /> חזרה לחנות
          </Link>
        </div>
      </div>
    );
  }

  const category = shopCategories.find((c) => c.id === product.category);

  const priceNum = (p: Product) => parseInt(p.price.replace(/\D/g, ''));

  const addProduct = (p: Product, markBundle = false) => {
    addToCart({ id: p.id, name: p.name, price: priceNum(p), quantity: 1, image: p.image });
    if (markBundle) {
      setAddedItems((prev) => ({ ...prev, [p.id]: true }));
      setTimeout(() => setAddedItems((prev) => ({ ...prev, [p.id]: false })), 2000);
    }
  };

  const handleAddMain = () => {
    addProduct(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const recommendedIds = productRecommendations[product.id] ?? [];
  const recommendedProducts = recommendedIds
    .map((rid) => productsData.find((p) => p.id === rid))
    .filter((p): p is Product => Boolean(p));
  const related = productsData.filter((p) => p.id !== product.id && p.category === product.category);
  // Bundle section requires exactly 2 upsells (3-product bundle). If product has 2 recommendations use them.
  const upsells = recommendedProducts.length >= 2 ? recommendedProducts.slice(0, 2) : [];

  const bundlePrice =
    priceNum(product) + upsells.reduce((s, p) => s + priceNum(p), 0);
  const bundleDiscounted = Math.round(bundlePrice * 0.85);


  const handleAddBundle = () => {
    addProduct(product);
    upsells.forEach((p) => addProduct(p));
    setBundleAdded(true);
    setTimeout(() => setBundleAdded(false), 2000);
  };

  const usps = [
    { icon: Leaf, label: '100% רכיבים טבעיים' },
    { icon: ShieldCheck, label: 'ללא חומרים אגרסיביים' },
    { icon: Droplets, label: 'מבוסס שמנים וצמחי מרפא' },
    { icon: FlaskConical, label: 'נרקח בעבודת יד' },
  ];

  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image,
    brand: { '@type': 'Brand', name: 'RIHAM' },
    offers: {
      '@type': 'Offer',
      price: String(priceNum(product)),
      priceCurrency: clinicInfo.currency,
      availability: 'https://schema.org/InStock',
      url: `${clinicInfo.url}/shop/${product.id}`,
      seller: providerRef,
    },
  };
  const faqJsonLd = product.faqs && product.faqs.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: product.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }
    : null;

  return (
    <>
      <Seo
        title={`${product.name} | RIHAM Shop`}
        description={product.description.slice(0, 155)}
        path={`/shop/${product.id}`}
        image={product.image}
        type="product"
        jsonLd={faqJsonLd ? [productJsonLd, faqJsonLd] : productJsonLd}
      />
    <div className="bg-[#EAE6D7] text-[#412018] relative min-h-screen pb-px">
      <div className="fixed inset-0 opacity-40 bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none mix-blend-multiply z-0"></div>

      {/* HERO — minimal: breadcrumb + category */}
      <section className="relative h-[55vh] min-h-[420px] w-full overflow-hidden">
        {category?.mediaType === 'video' ? (
          <LazyVideo
            src={category.media}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
           aria-hidden="true" />
        ) : (
          <div className="absolute inset-0 bg-[#412018]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#412018] via-[#412018]/70 to-[#412018]/30" />

        <div className="relative z-10 h-full container mx-auto px-6 flex flex-col justify-end pb-14" dir="rtl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 mb-5 text-[#fbbf25]/90 text-xs tracking-[0.3em] uppercase font-bold">
              <Link to="/" className="hover:text-[#fbbf25] transition-colors">RIHAM</Link>
              <span className="opacity-40">/</span>
              <Link to="/shop" className="hover:text-[#fbbf25] transition-colors">הבוטיק</Link>
              <span className="opacity-40">/</span>
              <span className="text-[#EAE6D7]/80">{category?.label}</span>
            </div>
            <p className="font-serif text-[#EAE6D7] text-3xl md:text-4xl lg:text-5xl leading-[1.05]">
              {category?.label}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-6 relative z-10">
        {/* MAIN BLOCK */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 -mt-24 relative z-20 mb-20">
          {/* Image + USPs */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-6 lg:sticky lg:top-28 self-start"
          >
            <div className="bg-white/80 backdrop-blur border border-[#412018]/10 overflow-hidden">
              {(() => {
                const images = ((product as any).images as string[] | undefined) ?? [product.image];
                const [activeIdx, setActiveIdx] = [productGalleryIdx, setProductGalleryIdx];
                const active = images[activeIdx] ?? product.image;
                return (
                  <>
                    <div className="aspect-[4/5] relative group">
                      <img
                        src={active}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-[2000ms] ease-luxury group-hover:scale-105"
                      />
                    </div>
                    {images.length > 1 && (
                      <div className="flex gap-2 p-3 overflow-x-auto bg-white/60 border-t border-[#412018]/10">
                        {images.map((url, i) => (
                          <button
                            key={url + i}
                            type="button"
                            onClick={() => setActiveIdx(i)}
                            className={`shrink-0 w-16 h-20 overflow-hidden border ${
                              i === activeIdx
                                ? 'border-[#67392b] ring-2 ring-[#67392b]/30'
                                : 'border-[#412018]/15 hover:border-[#412018]/40'
                            } transition`}
                            aria-label={`תמונה ${i + 1}`}
                          >
                            <img src={url} alt="" className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
              {/* USP icons row — joined to image */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#412018]/10 border-t border-[#412018]/10" dir="rtl">
                {usps.map((u) => (
                  <div
                    key={u.label}
                    className="bg-white/70 backdrop-blur px-3 py-5 flex flex-col items-center text-center gap-2"
                  >
                    <u.icon size={20} className="text-[#67392b]" />
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#412018]/80 leading-tight">
                      {u.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Info */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="lg:col-span-6"
            dir="rtl"
          >
            {/* Title + price card */}
            <div className="bg-white/80 backdrop-blur border border-[#412018]/10 mb-8 p-6 md:p-8">
              {/* In-stock badge */}
              <div className="inline-flex items-center gap-3 mb-7 pr-4 pl-5 py-2.5 bg-[#fbbf25]/15 border border-[#fbbf25]/50 rounded-full">
                <FlaskConical size={20} className="text-[#67392b] shrink-0" strokeWidth={1.6} />
                <span className="font-serif italic text-[#412018] text-[18px] md:text-[22px] leading-none">
                  במלאי · מוכן למשלוח
                </span>
              </div>

              <h1 className="font-serif text-[#412018] leading-[0.95] tracking-wide text-5xl md:text-5xl lg:text-6xl mb-4">
                {product.name}
              </h1>
              <p className="text-[16px] md:text-[17px] text-[#412018]/75 font-light leading-[1.7]">
                {product.tagline}
              </p>

              <CollapsibleDescription paragraphs={product.longDescription} />

              <div className="flex items-end justify-between mt-8 mb-6">
                <div>
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#67392b] font-bold block mb-2">
                    מחיר
                  </span>
                  <div className="font-sans font-light tabular-nums text-[#412018] text-3xl md:text-4xl leading-none flex items-baseline gap-3">
                    <span>{product.price}</span>
                    {(product as any).compareAtPrice && (
                      <span className="text-[#412018]/45 text-xl md:text-2xl line-through">
                        {(product as any).compareAtPrice}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#67392b] font-bold block mb-2">
                    נפח
                  </span>
                  <div className="font-sans font-light tabular-nums text-[#412018] text-xl md:text-2xl leading-none">
                    {product.size}
                  </div>
                </div>
              </div>

              <button
                onClick={handleAddMain}
                className={`w-full flex items-center justify-center gap-3 px-8 py-5 uppercase tracking-[0.3em] font-bold text-xs transition-all duration-500 ${
                  added
                    ? 'bg-green-700 text-white'
                    : 'bg-[#412018] text-[#fbbf25] hover:bg-[#67392b]'
                }`}
              >
                {added ? (
                  <>
                    <Check size={18} />
                    <span>נוסף לסל</span>
                  </>
                ) : (
                  <>
                    <ShoppingBag size={18} />
                    <span>הוספה לסל</span>
                  </>
                )}
              </button>

              <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-[#412018]/65 tracking-wide">
                <Sparkles size={12} className="text-[#67392b]" />
                <span>משלוח מהיר · משלוח חינם מעל ₪400</span>
              </div>
            </div>

            {/* Benefits — concise inline list */}
            <div className="mb-10">
              <h2 className="font-serif text-[#412018] text-[2.2rem] md:text-[2.4rem] lg:text-[3rem] leading-[1.15] mb-6">
                למה זה עובד
              </h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {product.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[#412018]/80 text-[14px] leading-relaxed">
                    <Check size={14} className="text-[#67392b] mt-1 shrink-0" strokeWidth={2.4} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>

        {/* INGREDIENTS / USAGE / PACKAGING */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-32" dir="rtl">
          {product.ingredients && (
            <div className="bg-white/70 backdrop-blur border border-[#412018]/10 p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <FlaskConical size={20} className="text-[#67392b]" />
                <h2 className="font-serif text-[#412018] text-[2.2rem] md:text-[2.4rem] lg:text-[3rem] leading-[1.15]">רכיבים מובילים</h2>
              </div>
              <p className="text-[#412018]/75 font-light leading-relaxed whitespace-pre-line">
                {product.ingredients}
              </p>
            </div>
          )}
          {product.usage && (
            <div className="bg-white/70 backdrop-blur border border-[#412018]/10 p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <HandHeart size={20} className="text-[#67392b]" />
                <h2 className="font-serif text-[#412018] text-[2.2rem] md:text-[2.4rem] lg:text-[3rem] leading-[1.15]">אופן השימוש</h2>
              </div>
              <p className="text-[#412018]/75 font-light leading-relaxed whitespace-pre-line">
                {product.usage}
              </p>
            </div>
          )}
          <div className="bg-white/70 backdrop-blur border border-[#412018]/10 p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Package size={20} className="text-[#67392b]" />
              <h2 className="font-serif text-[#412018] text-[2.2rem] md:text-[2.4rem] lg:text-[3rem] leading-[1.15]">אריזה</h2>
            </div>
            <p className="text-[#412018]/75 font-light leading-relaxed">
              {product.size} · אריזה הניתנת למיחזור · נרקח בעבודת יד במעבדה שלנו
            </p>
          </div>
        </div>

        {/* HOW IT WORKS — custom order journey */}
        <section className="relative py-8 md:py-16 mb-16 md:mb-24" dir="rtl">
          <div className="text-center max-w-[820px] mx-auto mb-12 md:mb-20">
            <p className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-5 md:mb-7">
              The Process
            </p>
            <h2 className="font-serif leading-[0.92] tracking-wide text-[3.2rem] sm:text-6xl md:text-7xl lg:text-[6.5rem] mb-6 md:mb-8">
              <span className="text-[#412018]">איך זה </span>
              <span className="text-[#67392b] italic">עובד</span>
            </h2>
            <div className="w-12 h-px bg-[#67392b] mx-auto mb-6 md:mb-8" />
            <p className="text-[#412018]/70 font-light text-[15px] md:text-lg leading-relaxed">
              ארבעה שלבים פשוטים מההזמנה ועד שהמוצר מגיע לדלת שלך — מוכן לשימוש.
            </p>
          </div>

          {(() => {
            const steps = [
              {
                icon: ClipboardList,
                step: '01',
                title: 'בוחרים מוצר',
                desc: 'בוחרים את המוצר מתוך הקולקציה — מוכן במלאי לשימוש מיידי.',
              },
              {
                icon: ShoppingBag,
                step: '02',
                title: 'הוספה לסל',
                desc: 'מוסיפים לסל ומשלימים הזמנה בתשלום מאובטח ופשוט.',
              },
              {
                icon: MessageCircle,
                step: '03',
                title: 'אישור ההזמנה',
                desc: 'מקבלים אישור מיידי במייל עם פרטי ההזמנה והמשלוח.',
              },
              {
                icon: Truck,
                step: '04',
                title: 'משלוח עד אליך',
                desc: 'המוצר נארז בקפידה ונשלח אלייך תוך 2–4 ימי עסקים, מוכן לשימוש.',
              },
            ];
            return (
              <>
                {/* DESKTOP / TABLET — horizontal grid */}
                <div className="hidden sm:block relative max-w-[1100px] mx-auto">
                  <div className="hidden lg:block absolute top-[44px] right-[10%] left-[10%] h-px bg-gradient-to-l from-transparent via-[#67392b]/35 to-transparent" />
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6 relative">
                    {steps.map((s, i) => (
                      <motion.div
                        key={s.step}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-5% 0px' }}
                        transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                        className="relative flex flex-col items-center text-center group"
                      >
                        <div className="relative mb-7">
                          <div className="w-[88px] h-[88px] rounded-full bg-[#EAE6D7] border border-[#412018]/15 flex items-center justify-center shadow-[0_8px_30px_-12px_rgba(65,32,24,0.25)] group-hover:bg-[#412018] transition-colors duration-500">
                            <s.icon size={32} strokeWidth={1.4} className="text-[#67392b] group-hover:text-[#fbbf25] transition-colors duration-500" />
                          </div>
                          <span className="absolute -top-2 -left-2 bg-[#412018] text-[#fbbf25] text-[10px] tracking-[0.2em] font-bold px-2.5 py-1 rounded-full">
                            {s.step}
                          </span>
                        </div>
                        <h3 className="font-serif text-[#412018] text-[1.7rem] md:text-[1.9rem] leading-[1.1] mb-3">{s.title}</h3>
                        <p className="text-[#412018]/70 font-light text-[14.5px] md:text-[15px] leading-[1.7] max-w-[260px]">{s.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* MOBILE — vertical timeline with watermark numbers */}
                <div className="sm:hidden relative max-w-[440px] mx-auto pr-2">
                  {/* Vertical dashed line on the right (RTL) */}
                  <div
                    className="absolute top-3 bottom-3 right-[34px] w-px"
                    style={{
                      backgroundImage:
                        'linear-gradient(to bottom, #67392b 0 6px, transparent 6px 14px)',
                      backgroundSize: '1px 14px',
                      backgroundRepeat: 'repeat-y',
                      opacity: 0.35,
                    }}
                  />
                  <div className="space-y-6">
                    {steps.map((s, i) => (
                      <motion.div
                        key={s.step}
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: '-5% 0px' }}
                        transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                        className="relative flex items-stretch gap-4"
                      >
                        {/* Icon node */}
                        <div className="relative shrink-0 z-10">
                          <div className="w-[68px] h-[68px] rounded-full bg-[#EAE6D7] border border-[#412018]/20 flex items-center justify-center shadow-[0_6px_22px_-10px_rgba(65,32,24,0.35)] ring-4 ring-[#EAE6D7]">
                            <s.icon size={26} strokeWidth={1.4} className="text-[#67392b]" />
                          </div>
                        </div>

                        {/* Card */}
                        <div className="relative flex-1 px-1 py-2 overflow-hidden">
                          {/* Watermark step number */}
                          <span
                            className="absolute -top-4 left-0 font-serif italic text-[6.5rem] leading-none text-[#67392b]/20 select-none pointer-events-none"
                            aria-hidden
                          >
                            {s.step}
                          </span>
                          <div className="relative">
                            <h3 className="font-serif text-[#412018] text-[2.2rem] leading-[1.1] mb-3">
                              {s.title}
                            </h3>
                            <p className="text-[#412018]/75 font-light text-[14.5px] leading-[1.7]">
                              {s.desc}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </>
            );
          })()}
        </section>

        {/* FAQ — service-page style */}
        <section className="relative py-8 md:py-16 mb-12 md:mb-20" dir="rtl">
          <div className="text-center max-w-[820px] mx-auto mb-12 md:mb-20">
            <p className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-5 md:mb-7">
              FAQ
            </p>
            <h2 className="font-serif leading-[0.92] tracking-wide text-[3.6rem] sm:text-6xl md:text-7xl lg:text-[6.5rem] mb-6 md:mb-8">
              <span className="text-[#412018]">שאלות </span>
              <span className="text-[#67392b] italic">נפוצות</span>
            </h2>
            <div className="w-12 h-px bg-[#67392b] mx-auto mb-6 md:mb-8" />
            <p className="text-[#412018]/70 font-light text-[15px] md:text-lg leading-relaxed">
              כל מה שחשוב לדעת על המוצר — ואם נשארה שאלה פתוחה, תמיד אפשר לפנות אלינו.
            </p>
          </div>

          <div className="border-y border-[#412018]/15 max-w-[1000px] mx-auto">
            {product.faqs.map((f, i) => (
              <FAQItem key={i} q={f.q} a={f.a} index={i} />
            ))}
          </div>
        </section>

        {/* Wide banner — full-bleed video with parallax */}
        <ParallaxVideoBanner src="https://cdn.shopify.com/videos/c/o/v/0971090ff8e54fdcaff90d1707f5caa0.mp4" />
        {/* BUNDLE — shop-page style, placed under FAQ */}
        {upsells.length === 2 && (
          <section className="mb-32" dir="rtl">
            <div className="text-center max-w-[820px] mx-auto mb-12 md:mb-16">
              <p className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-5 md:mb-7">
                Holistic Bundle
              </p>
              <h2 className="font-serif leading-[0.92] tracking-wide text-5xl md:text-6xl lg:text-7xl mb-6 md:mb-8">
                <span className="text-[#412018]">השלימי את </span>
                <span className="text-[#67392b] italic">הריטואל</span>
              </h2>
              <div className="w-12 h-px bg-[#67392b] mx-auto mb-6 md:mb-8" />
              <p className="text-[#412018]/75 font-light text-[15px] md:text-lg leading-relaxed">
                שלוש פורמולות שעובדות יחד לתוצאות מקסימליות — חסכו 15% על המארז המלא.
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-5% 0px' }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-stretch"
            >
              {/* Composition / images — mobile order: middle */}
              <div className="lg:col-span-7 order-2 lg:order-none">
                <div className="relative bg-[#412018] aspect-[4/3] md:aspect-[16/10] overflow-hidden">
                  <div className="absolute inset-0 grid grid-cols-3 gap-px bg-[#412018]/40">
                    {[product, ...upsells].slice(0, 3).map((p, i) => (
                      <div key={i} className="relative bg-white overflow-hidden">
                        <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105" />
                      </div>
                    ))}
                  </div>
                  <div className="absolute top-5 right-5 bg-[#fbbf25] text-[#412018] px-3 py-1 text-[10px] font-bold tracking-[0.25em] uppercase">
                    חיסכון 15%
                  </div>
                </div>
              </div>

              {/* Details — split for mobile reorder */}
              <div className="lg:col-span-5 flex flex-col lg:justify-center contents lg:flex">
                {/* TOP block — above image on mobile */}
                <div className="order-1 lg:order-none">
                  <span className="text-[10px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-4 block">
                    מארז משתלם
                  </span>
                  <h3 className="font-serif text-[#412018] leading-[1.15] tracking-wide text-[2.2rem] md:text-[2.4rem] lg:text-[3rem] mb-4">
                    הריטואל המלא
                  </h3>
                  <p className="text-[#67392b] italic text-base md:text-lg mb-5">
                    שלושה מוצרים שנועדו לעבוד יחד
                  </p>
                  <p className="text-[#412018]/75 font-light leading-relaxed text-[15px] md:text-base mb-7 max-w-xl">
                    שלבו את {product.name} עם שני המוצרים המשלימים שלו לתוצאות מקסימליות.
                  </p>

                  <ul className="space-y-2.5 mb-2 lg:mb-8">
                    {[product, ...upsells].map((p) => (
                      <li key={p.id} className="flex items-center gap-3 text-[#412018]/85 text-sm">
                        <span className="w-6 h-6 rounded-full bg-[#fbbf25]/20 border border-[#fbbf25]/50 flex items-center justify-center shrink-0">
                          <Droplet size={11} className="text-[#67392b] fill-[#67392b]/30" strokeWidth={2} />
                        </span>
                        <Link to={`/shop/${p.id}`} className="hover:text-[#67392b] transition-colors">
                          {p.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* BOTTOM block — below image, price + CTA inline */}
                <div className="order-3 lg:order-none mt-6 lg:mt-0 flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-baseline gap-3">
                    <span className="font-sans font-light text-3xl md:text-4xl text-[#412018] tabular-nums leading-none">
                      ₪{bundleDiscounted}
                    </span>
                    <span className="text-[#412018]/40 line-through text-base">₪{bundlePrice}</span>
                  </div>
                  <button
                    onClick={handleAddBundle}
                    className={`group/cta relative overflow-hidden px-7 py-3.5 transition-colors duration-500 ${
                      bundleAdded ? 'bg-green-600 text-white' : 'bg-[#412018] text-[#fbbf25]'
                    }`}
                  >
                    <span className="absolute inset-0 bg-[#fbbf25] translate-y-full group-hover/cta:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.7,0,0.2,1)]" />
                    <span className={`relative flex items-center gap-2.5 transition-colors duration-500 ${bundleAdded ? '' : 'group-hover/cta:text-[#412018]'}`}>
                      {bundleAdded ? <Check size={14} /> : <ShoppingBag size={14} />}
                      <span className="text-[11px] font-bold uppercase tracking-[0.28em]">
                        {bundleAdded ? 'נוסף לסל' : 'הוספה לסל'}
                      </span>
                    </span>
                  </button>
                </div>
              </div>
            </motion.div>
          </section>
        )}

        {/* RECOMMENDED — carousel */}
        {(() => {
          const recommended = recommendedProducts;
          if (recommended.length === 0) return null;
          return (
            <RecommendedCarousel
              products={recommended}
              addProduct={addProduct}
              addedItems={addedItems}
            />
          );
        })()}

      </div>

      {/* STICKY BOTTOM BAR */}
      <AnimatePresence>
        {showStickyBar && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-0 left-0 right-0 z-40 bg-[#EAE6D7]/95 backdrop-blur-md border-t border-[#412018]/15"
            dir="rtl"
          >
            <div className="container mx-auto px-4 md:px-6 max-w-[1400px]">
              <div className="flex items-center gap-3 md:gap-8 py-3 md:py-5">
                <div className="hidden md:block flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#67392b] font-bold mb-1.5">
                    {category?.label}
                  </p>
                  <p className="font-serif text-[#412018] text-2xl lg:text-[1.9rem] xl:text-[2.2rem] leading-[1.05] truncate">
                    {product.name}
                  </p>
                </div>

                <div className="flex flex-col items-start shrink-0">
                  <span className="text-[9px] uppercase tracking-[0.25em] text-[#412018]/55">נפח</span>
                  <span className="font-sans font-light text-[#412018] text-[1rem] tabular-nums leading-tight">
                    {product.size}
                  </span>
                </div>

                <span className="w-px h-8 bg-[#412018]/20 shrink-0" />

                <div className="flex flex-col items-start shrink-0">
                  <span className="text-[9px] uppercase tracking-[0.25em] text-[#412018]/55">מחיר</span>
                  <span className="font-sans font-light text-[#412018] text-[1rem] tabular-nums leading-tight">
                    {product.price}
                  </span>
                </div>

                <button
                  onClick={handleAddMain}
                  className={`group/cta relative overflow-hidden px-4 md:px-6 py-3 mr-auto shrink-0 transition-colors duration-300 ${
                    added ? 'bg-green-700 text-white' : 'bg-[#412018] text-[#fbbf25]'
                  }`}
                >
                  {!added && (
                    <span className="absolute inset-0 bg-[#fbbf25] translate-y-full group-hover/cta:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.7,0,0.2,1)]" />
                  )}
                  <span className={`relative flex items-center justify-center gap-2 ${added ? '' : 'group-hover/cta:text-[#412018]'} transition-colors duration-500`}>
                    {added ? <Check size={14} /> : <ShoppingBag size={14} strokeWidth={1.6} />}
                    <span className="text-[10px] font-bold uppercase tracking-[0.28em]">
                      {added ? 'נוסף לסל' : 'הוספה לסל'}
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </>
  );
};

// ===== Recommended carousel =====
const RecommendedCarousel: React.FC<{
  products: Product[];
  addProduct: (p: Product, mark?: boolean) => void;
  addedItems: { [k: string]: boolean };
}> = ({ products, addProduct, addedItems }) => {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const sectionRef = React.useRef<HTMLElement>(null);
  const draggingRef = React.useRef(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [revealed, setRevealed] = React.useState(false);

  React.useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const scrollByCard = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('[data-card]');
    const step = card ? card.offsetWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  // Pointer-based drag-to-scroll with momentum — same mechanic as InstagramReels
  React.useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    let isDown = false;
    let startX = 0;
    let startScroll = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;
    let momentumId = 0;
    let pendingScroll = 0;
    let rafId = 0;

    const cancelMomentum = () => {
      if (momentumId) cancelAnimationFrame(momentumId);
      momentumId = 0;
    };
    const applyScroll = () => {
      el.scrollLeft = pendingScroll;
      rafId = 0;
    };

    const onDown = (e: PointerEvent) => {
      cancelMomentum();
      isDown = true;
      draggingRef.current = false;
      setIsDragging(false);
      startX = lastX = e.clientX;
      lastT = performance.now();
      startScroll = el.scrollLeft;
      velocity = 0;
    };
    const onMove = (e: PointerEvent) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (!draggingRef.current && Math.abs(dx) > 4) {
        draggingRef.current = true;
        setIsDragging(true);
        try { el.setPointerCapture(e.pointerId); } catch {}
      }
      if (!draggingRef.current) return;
      const now = performance.now();
      const dt = Math.max(1, now - lastT);
      velocity = (e.clientX - lastX) / dt;
      lastX = e.clientX;
      lastT = now;
      pendingScroll = startScroll - dx;
      if (!rafId) rafId = requestAnimationFrame(applyScroll);
    };
    const onUp = (e: PointerEvent) => {
      if (!isDown) return;
      isDown = false;
      try { el.releasePointerCapture(e.pointerId); } catch {}

      if (draggingRef.current && Math.abs(velocity) > 0.05) {
        let v = velocity * 16;
        const friction = 0.94;
        const step = () => {
          v *= friction;
          el.scrollLeft -= v;
          if (Math.abs(v) > 0.4) {
            momentumId = requestAnimationFrame(step);
          } else {
            momentumId = 0;
          }
        };
        momentumId = requestAnimationFrame(step);
      }
      setTimeout(() => {
        draggingRef.current = false;
        setIsDragging(false);
      }, 60);
    };
    const onClickCapture = (e: MouseEvent) => {
      if (draggingRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    el.addEventListener('click', onClickCapture, true);
    return () => {
      cancelMomentum();
      if (rafId) cancelAnimationFrame(rafId);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.removeEventListener('click', onClickCapture, true);
    };
  }, []);

  return (
    <section ref={sectionRef} className="border-t border-[#412018]/10 pt-16 pb-24 overflow-hidden" dir="rtl">
      <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
        <div
          className={`transition-all duration-[1100ms] ease-luxury ${revealed ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-8 blur-[6px]'}`}
        >
          <span className="text-[#67392b] text-xs tracking-[0.4em] uppercase font-bold block mb-2 overflow-hidden">
            <span
              className={`inline-block transition-transform duration-[1100ms] ease-luxury ${revealed ? 'translate-y-0' : 'translate-y-full'}`}
            >
              מומלץ עבורך
            </span>
          </span>
          <h2 className="font-serif text-[#412018] text-[2.2rem] md:text-[2.4rem] lg:text-[3rem] leading-[1.15]">
            מוצרים שעשויים לעניין אותך
          </h2>
        </div>
        <div className={`flex items-center gap-3 transition-all duration-[1100ms] ease-luxury delay-200 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Link
            to="/shop"
            className="hidden md:flex items-center gap-2 text-sm font-bold tracking-[0.2em] uppercase text-[#412018] hover:text-[#67392b] transition-colors"
          >
            לכל הבוטיק <ArrowLeft size={16} />
          </Link>
          <div className="flex gap-2">
            <button
              onClick={() => scrollByCard(-1)}
              aria-label="הקודם"
              className="w-11 h-11 rounded-full border border-[#412018]/25 text-[#412018] hover:bg-[#412018] hover:text-[#fbbf25] transition-colors flex items-center justify-center"
            >
              <ArrowLeft size={16} className="rotate-180" />
            </button>
            <button
              onClick={() => scrollByCard(1)}
              aria-label="הבא"
              className="w-11 h-11 rounded-full border border-[#412018]/25 text-[#412018] hover:bg-[#412018] hover:text-[#fbbf25] transition-colors flex items-center justify-center"
            >
              <ArrowLeft size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="relative -mx-6 md:mx-0">
        <div
          ref={trackRef}
          className={`flex gap-6 overflow-x-auto px-6 md:px-0 pt-2 pb-10 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden select-none touch-pan-x overscroll-x-contain ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          style={{ scrollBehavior: 'auto' }}
        >
          {products.map((item, index) => (
            <div
              key={item.id}
              data-card
              style={{
                transitionDelay: revealed ? `${300 + index * 110}ms` : '0ms',
              }}
              className={`group bg-white border border-[#412018]/5 overflow-hidden flex flex-col shadow-sm hover:shadow-2xl hover:shadow-[#67392b]/15 shrink-0 w-[78%] sm:w-[46%] md:w-[32%] lg:w-[300px] xl:w-[320px] transition-all duration-[1100ms] ease-luxury ${revealed ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-16 blur-[8px]'}`}
            >
              <Link to={`/shop/${item.id}`} draggable={false} className="aspect-square overflow-hidden block relative">
                <img
                  src={item.image}
                  alt={item.name}
                  draggable={false}
                  className="w-full h-full object-cover transition-transform duration-[1500ms] ease-luxury group-hover:scale-110 pointer-events-none"
                />
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-3 py-1 flex items-center gap-2">
                  <Leaf size={12} className="text-green-700" />
                  <span className="text-[10px] uppercase tracking-wider text-[#412018] font-bold">100% טבעי</span>
                </div>
              </Link>

              <div className="p-6 flex flex-col flex-1">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#67392b] font-bold mb-2">
                  {item.sub}
                </p>
                <div className="flex justify-between items-baseline gap-3 mb-3">
                  <h3 className="font-serif text-2xl md:text-3xl text-[#412018] leading-[0.95]">
                    {item.name}
                  </h3>
                  <span className="font-sans font-light tabular-nums text-lg whitespace-nowrap text-[#412018]">{item.price}</span>
                </div>
                <p className="text-[13px] text-[#412018]/70 font-light leading-relaxed mb-6 line-clamp-3 flex-1">
                  {item.description}
                </p>
              </div>

              <div className="flex border-t border-[#412018]/10 mt-auto">
                <Link
                  to={`/shop/${item.id}`}
                  className="flex-1 bg-[#F9F7F2] hover:bg-[#EAE6D7] text-[#412018] py-4 flex items-center justify-center gap-2 border-l border-[#412018]/10 transition-colors text-[10px] font-bold tracking-[0.2em] uppercase"
                >
                  <Plus size={12} /> פרטים
                </Link>
                <button
                  onClick={() => addProduct(item, true)}
                  className={`flex-[2] py-4 flex items-center justify-center gap-2 transition-colors text-[10px] font-bold tracking-[0.2em] uppercase ${
                    addedItems[item.id]
                      ? 'bg-green-700 text-white'
                      : 'bg-[#412018] hover:bg-[#67392b] text-[#fbbf25]'
                  }`}
                >
                  {addedItems[item.id] ? <Check size={14} /> : <ShoppingBag size={14} />}
                  <span>{addedItems[item.id] ? 'נוסף' : 'הוספה לסל'}</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
